/**
 * ChatView — family chat with channel scoping (family / adults / DM).
 * Per app-codex: surface "chat.list". Behaviour: chat.send, chat.subscribe.
 * Per app.pact.rule.chat.channel-scoping.007: RLS enforces channels.
 * Dev mode: uses devDb.
 */
import { useEffect, useState, useRef } from 'react'
import { useApp } from '../store'
import { sendMessage, fetchMessages, subscribeMessages, type DbMessage } from '../db'
import { devDb } from '../devMode'
import { Send, Users, Shield, MessageSquare } from 'lucide-react'

const CHANNELS = [
  { id: 'family' as const, label: 'Family', icon: Users },
  { id: 'adults' as const, label: 'Adults', icon: Shield },
  { id: 'dm' as const, label: 'Direct', icon: MessageSquare },
] as const

export function ChatView() {
  const { currentMember, chatChannel, setChatChannel, dev } = useApp()
  const [messages, setMessages] = useState<DbMessage[]>([])
  const [input, setInput] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    load()
    if (dev) return
    const sub = subscribeMessages((msg) => {
      if (msg.channel === chatChannel) {
        setMessages((prev) => [...prev, msg])
      }
    }, chatChannel)
    return () => {
      try { sub?.unsubscribe?.() } catch {}
    }
  }, [chatChannel, dev])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  async function load() {
    if (dev) {
      const { data } = await devDb.messages.list()
      setMessages(data ?? [])
    } else {
      const { data } = await fetchMessages(chatChannel)
      setMessages(data ?? [])
    }
  }

  async function send(e: React.FormEvent) {
    e.preventDefault()
    if (!input.trim() || !currentMember) return
    const body = input.trim()
    setInput('')
    if (dev) {
      await devDb.messages.send(chatChannel, body, currentMember.id)
    } else {
      await sendMessage(chatChannel, body, currentMember.id)
    }
    load()
  }

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-skin-text">Chat</h1>
        <p className="text-slate-500 mt-1">A quiet place to talk to each other.</p>
      </header>

      <div className="flex items-center gap-1 mb-4 overflow-x-auto">
        {CHANNELS.map(({ id, label, icon: Icon }) => {
          const active = chatChannel === id
          return (
            <button
              key={id}
              onClick={() => setChatChannel(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium min-h-touch ${
                active ? 'bg-garden-100 text-garden-800' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          )
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl flex flex-col h-[500px]">
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-12">No messages yet. Say something kind.</div>
          )}
          {messages.map((m) => (
            <div key={m.id} className="flex flex-col">
              <div className="text-xs text-slate-500 mb-0.5">
                {m.sender.replace(/^dev-/, '').replace(/^./, (c) => c.toUpperCase())}
                <span className="text-slate-400 ml-1.5">· {new Date(m.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="bg-slate-50 rounded-2xl rounded-tl-none px-3 py-2 inline-block max-w-[85%]">
                <p className="text-sm chat-prose">{m.body}</p>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="border-t border-slate-200 p-3 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Write a kind word…"
            className="flex-1 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-garden-500 min-h-touch"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            className="bg-garden-800 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-garden-900 disabled:opacity-50 min-h-touch flex items-center gap-1.5"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  )
}
